<template>
  <div id="bottom">
    <q-table
      id="table"
      dark
      grid
      grid-header
      title="Inventory"
      :rows="rows"
      :columns="columns"
      row-key="name"
      :filter="filter"
      v-model:pagination="pagination"
      :rows-per-page-options="[24, 48, 120]"
      rows-per-page-label="Story Cards per page :"
      no-data-label="Add a story card to your inventory"
      no-results-label="No Story Card matches these criteria"
    >
      <template v-slot:header>
        <div id="header" class="row justify-between">
          <div class="col-xs-4 col-md-2">
            <div class="sort-btns-div">
              <q-btn
                class="sort-btn"
                v-bind:style="[
                  activeSort == 'name'
                    ? { 'background-color': '#eeeeee36', color: '#ffffff' }
                    : {},
                ]"
                v-model="nameSort"
                unelevated
                no-caps
                :padding="btnPad"
                @click="sortByName()"
              >
                <div>Name</div>
                <q-icon :name="nameArrowIcon"> </q-icon>
              </q-btn>
            </div>
          </div>
          <div class="col-xs-4 col-md-2">
            <div class="sort-btns-div">
              <q-btn
                class="sort-btn"
                v-bind:style="[
                  activeSort == 'level'
                    ? { 'background-color': '#eeeeee36', color: '#ffffff' }
                    : {},
                ]"
                v-model="levelSort"
                unelevated
                no-caps
                :padding="btnPad"
                @click="sortByLevel()"
              >
                <div>Level</div>
                <q-icon :name="levelArrowIcon"> </q-icon>
              </q-btn>
            </div>
          </div>
          <div class="col-xs-4 col-md-2">
            <div class="sort-btns-div">
              <q-btn
                class="sort-btn"
                v-bind:style="[
                  mlbFilter != 'All'
                    ? { 'background-color': '#eeeeee36', color: '#ffffff' }
                    : {},
                ]"
                v-model="mlbFilter"
                unelevated
                no-caps
                :padding="btnPad"
                @click="filterByMLB()"
              >
                <div>{{ mlbFilter }}</div>
                <q-icon :name="mlbIcon"> </q-icon>
              </q-btn>
            </div>
          </div>
          <div class="col-xs-4 col-md-2">
            <div class="sort-select">
              <q-select
                dark
                :dense="isMobile"
                clearable
                v-model="statSelect"
                label="Stat"
                :options="statOptions"
                options-dense
                filled
              >
              </q-select>
            </div>
          </div>
          <div class="col-xs-4 col-md-2">
            <div class="sort-select">
              <q-select
                dark
                :dense="isMobile"
                clearable
                v-model="buffSelect"
                label="Buff"
                :options="buffOptions"
                options-dense
                filled
              ></q-select>
            </div>
          </div>
          <div class="col-xs-4 col-md-2">
            <div class="sort-select">
              <q-select
                dark
                :dense="isMobile"
                clearable
                v-model="bulletSelect"
                label="Bullet"
                :options="bulletOptions"
                options-dense
                filled
              ></q-select>
            </div>
          </div>
          <div class="col-grow">
            <div class="display-toggle">
              <div>Display Ability</div>
              <q-toggle
                name="toggle"
                v-model="displayMode"
                color="primary"
                keep-color
                dark
                @update:model-value="switchDisplayMode"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:top-right>
        <q-input
          dark
          dense
          debounce="300"
          v-model="filter"
          placeholder="Search"
        >
          <template v-slot:append>
            <q-icon name="search" />
          </template>
        </q-input>
      </template>
      <template v-slot:no-data>
        <div class="full-width row flex-center text-subtitle1 q-pa-sm">
          <div v-if="empty">
            <div>Add a Story Card to your inventory</div>
          </div>
          <div v-else class="row flex-center q-gutter-md">
            <div>No Story Card matches these criteria</div>
            <q-btn
              id="reset-btn"
              color="primary"
              no-caps
              outline
              @click="resetFilters()"
            >
              <div>Reset Filters</div>
              <q-icon name="mdi-backspace-outline"> </q-icon>
            </q-btn>
          </div>
        </div>
      </template>
      <template v-slot:item="props">
        <q-intersection
          class="q-pa-xs col-xs-12 col-sm-6 col-md-4 col-lg-3"
          once
        >
          <story-card-item
            :display_toggle="displayModeObj"
            :story_card="props"
          ></story-card-item>
        </q-intersection>
      </template>
    </q-table>
  </div>
</template>

<style scoped>
#bottom {
  border-radius: 4px;
  background-color: #00000020;
  margin: 2vh 0 0 0;
  padding: 0 0.5vw;
}
#header {
  margin: 1vh 1vw;
}
#table {
  color: #fffffff0;
}
.sort-select {
  margin: 5px;
  min-width: 15%;
}
.sort-btns-div {
  display: flex;
  margin: 5px;
  min-width: 15%;
}
.sort-btns-div .q-icon,
#reset-btn .q-icon {
  margin-left: 6px;
}
.sort-btn {
  border-radius: 4px;
  font-size: 0.9rem;
  width: 100%;
  background-color: #ffffff12;
}
.display-toggle {
  display: flex;
  align-items: center;
  margin-right: 5px;
  margin-left: 5px;
  min-width: max(96px, 16%);
  font-weight: 400;
  color: #fffffff0;
}
@media only screen and (max-width: 500px) {
  .sort-select {
    margin-right: 2px;
    margin-left: 2px;
  }
  .sort-btns-div {
    margin-right: 2px;
    margin-left: 2px;
  }
}
</style>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";
import StoryCardItem from "./StoryCardItem.vue";

const myStorage = window.localStorage;
const $q = useQuasar();

const store = useStore();

const delay = (time) => {
  return new Promise((resolve) => setTimeout(resolve, time));
};

const fillList = () => {
  if (!store.state.sc.isLoadedStoryCardList && store.state.sc.isLoadedStoryCardData) {
    store.commit("sortStoryCards", displayPayload.value);
    store.commit("setLoadStoryCardList", true);
  }
};

// TODO remove ?
if (!myStorage.getItem("story_cards_list")) {
  myStorage.setItem("story_cards_list", "[]");
}
if (!store.state.sc.isLoadedStoryCardData) {
  store.dispatch("getStoryCardsData");
}
for (let millisecs = 100; millisecs <= 500; millisecs += 500) {
  delay(millisecs).then(() => fillList());
  if (store.state.sc.isLoadedStoryCardData) {
    continue;
  }
}

const columns = [
  {
    name: "name",
    required: true,
    label: "Name",
    align: "left",
    field: (row) => row.name,
    format: (val) => `${val}`,
    sortable: true,
  },
];

const rows = ref([]);

store.watch(
  () => store.getters.getStoryCardList,
  () => {
    rows.value = store.getters.getStoryCardList;
  }
);

const filter = ref("");

const displayPayload = ref({
  filter: {
    mlbFilter: "",
    statFilter: "",
    buffFilter: "",
    bulletFilter: "",
    rarityFilter: "",
  },
  primary_sort: {},
  secondary_sort: {},
});

const activeSort = ref("");

const nameSort = ref("");
const nameArrowIcon = ref("");

const displayMode = ref(null);
if (!myStorage.getItem("display_mode")) myStorage.setItem("display_mode", null);

const sortByName = () => {
  // update ui
  console.time("sort");
  if (nameSort.value === "") {
    nameSort.value = "d";
    nameArrowIcon.value = "mdi-arrow-down";
    activeSort.value = "name";
  } else if (nameSort.value === "d") {
    nameSort.value = "a";
    nameArrowIcon.value = "mdi-arrow-up";
    activeSort.value = "name";
  } else if (nameSort.value === "a") {
    nameSort.value = "";
    nameArrowIcon.value = "";
    activeSort.value = "";
  }
  if (!("nameSort" in displayPayload.value.primary_sort)) {
    displayPayload.value.secondary_sort = displayPayload.value.primary_sort;
  }
  displayPayload.value.primary_sort = { nameSort: nameSort.value };
  store.commit("sortStoryCards", displayPayload.value);
  console.timeEnd("sort");
};

const levelSort = ref("");
const levelArrowIcon = ref("");
const sortByLevel = () => {
  // update ui
  if (levelSort.value === "") {
    levelSort.value = "a";
    levelArrowIcon.value = "mdi-arrow-up";
    activeSort.value = "level";
  } else if (levelSort.value === "a") {
    levelSort.value = "d";
    levelArrowIcon.value = "mdi-arrow-down";
    activeSort.value = "level";
  } else if (levelSort.value === "d") {
    levelSort.value = "";
    levelArrowIcon.value = "";
    activeSort.value = "";
  }
  if (!("levelSort" in displayPayload.value.primary_sort)) {
    displayPayload.value.secondary_sort = displayPayload.value.primary_sort;
  }
  displayPayload.value.primary_sort = { levelSort: levelSort.value };
  store.commit("sortStoryCards", displayPayload.value);
};

const mlbFilter = ref("All");
const mlbIcon = ref("");
const filterByMLB = () => {
  // update ui
  if (mlbFilter.value === "All") {
    mlbFilter.value = "MLB";
    mlbIcon.value = "mdi-cards-diamond";
  } else if (mlbFilter.value === "MLB") {
    mlbFilter.value = "Non MLB";
    mlbIcon.value = "mdi-cards-diamond-outline";
  } else if (mlbFilter.value === "Non MLB") {
    mlbFilter.value = "All";
    mlbIcon.value = "";
  }
  displayPayload.value.filter.mlbFilter = mlbFilter.value;
  store.commit("sortStoryCards", displayPayload.value);
};

const statSelect = ref(null);
const statOptions = [
  "Health",
  "Yang ATK",
  "Yin ATK",
  "Agility",
  "Yang DEF",
  "Yin DEF",
];
watch(statSelect, () => {
  activeSort.value = "stat";
  displayPayload.value.secondary_sort = displayPayload.value.primary_sort;
  displayPayload.value.primary_sort = { statSort: statSelect.value };
  displayPayload.value.filter.statFilter = statSelect.value;
  store.commit("sortStoryCards", displayPayload.value);
});

const buffSelect = ref(null);
const buffOptions = [
  "Yang ATK",
  "Yin ATK",
  "Agility",
  "Yang DEF",
  "Yin DEF",
  "Accuracy",
  "Crit Accuracy",
  "Crit ATK",
  "Spirit Power",
  //...
];
watch(buffSelect, () => {
  activeSort.value = "buff";
  displayPayload.value.secondary_sort = displayPayload.value.primary_sort;
  displayPayload.value.primary_sort = { buffSort: buffSelect.value };
  displayPayload.value.filter.buffFilter = buffSelect.value;
  store.commit("sortStoryCards", displayPayload.value);
});

const bulletSelect = ref(null);
const bulletOptions = [
  "Sun",
  "Moon",
  "Fire",
  "Water",
  "Wood",
  "Metal",
  "Earth",
  "Star",
  "No-Element",
  "Body",
  "Energy",
  "Heavy",
  "Laser",
  "Light",
  "Liquid",
  "Missile",
  "Normal",
  "Ofuda",
  "Slash",
];
watch(bulletSelect, () => {
  activeSort.value = "bullet";
  displayPayload.value.secondary_sort = displayPayload.value.primary_sort;
  displayPayload.value.primary_sort = { bulletSort: bulletSelect.value };
  displayPayload.value.filter.bulletFilter = bulletSelect.value;
  store.commit("sortStoryCards", displayPayload.value);
});

const resetFilters = () => {
  mlbFilter.value = "All";
  mlbIcon.value = "";
  statSelect.value = null;
  buffSelect.value = null;
  bulletSelect.value = null;
  filter.value = "";

  displayPayload.value.filter = {
    mlbFilter: null,
    statFilter: null,
    buffFilter: null,
    bulletFilter: null,
    rarityFilter: null,
  };
  store.commit("sortStoryCards", displayPayload.value);
};

const switchDisplayMode = () => {
  let startTime = performance.now();
  store.commit("updateAllStoryCardsDisplayMode", displayMode.value);
  let endTime = performance.now();
  console.log(endTime - startTime);
};

store.watch(
  () => store.state.sc.currentToggleDisplayMode,
  () => {
    displayMode.value = store.state.sc.currentToggleDisplayMode;
  }
);

const updateUi = () => {
  /*       timeEnd(); */
  let startTime = performance.now();
  rows.value = store.getters.getStoryCardList;
  displayPayload.value = store.state.sc.currentSort;
  let f = store.state.sc.currentSort.filter;
  let ps = store.state.sc.currentSort.primary_sort;
  if ("mlbFilter" in f) {
    mlbFilter.value = f.mlbFilter;
    if (mlbFilter.value === "MLB") {
      mlbFilter.value = "MLB";
      mlbIcon.value = "mdi-cards-diamond";
    } else if (mlbFilter.value === "Non MLB") {
      mlbFilter.value = "Non MLB";
      mlbIcon.value = "mdi-cards-diamond-outline";
    } else {
      mlbFilter.value = "All";
      mlbIcon.value = "";
    }
  }
  if ("nameSort" in ps) {
    nameSort.value = ps.nameSort;
    if (nameSort.value === "d") {
      nameArrowIcon.value = "mdi-arrow-down";
      activeSort.value = "name";
    } else if (nameSort.value === "a") {
      nameArrowIcon.value = "mdi-arrow-up";
      activeSort.value = "name";
    } else {
      nameArrowIcon.value = "";
      activeSort.value = "";
    }
  } else if ("levelSort" in ps) {
    levelSort.value = ps.levelSort;
    if (levelSort.value === "d") {
      levelArrowIcon.value = "mdi-arrow-down";
      activeSort.value = "level";
    } else if (levelSort.value === "a") {
      levelArrowIcon.value = "mdi-arrow-up";
      activeSort.value = "level";
    } else {
      levelArrowIcon.value = "";
      activeSort.value = "";
    }
  } else if ("statSort" in ps) {
    statSelect.value = ps.statSort;
    activeSort.value = "stat";
  } else if ("buffSort" in ps) {
    buffSelect.value = ps.buffSort;
    activeSort.value = "buff";
  } else if ("bulletSort" in ps) {
    bulletSelect.value = ps.bulletSort;
    activeSort.value = "bullet";
  }
  if (myStorage.getItem("display_mode")) {
    displayMode.value = JSON.parse(myStorage.getItem("display_mode"));
  }
  let _payload = {
    fn: "StoryCardsList.updateUi",
    startTime: startTime,
  };
  store.commit("_updateCalls", _payload);
};

const pagination = ref({
  rowsPerPage: 48,
});
if (myStorage.getItem("pagination")) {
  pagination.value = JSON.parse(myStorage.getItem("pagination"));
}

watch(pagination, (newValue, oldValue) => {
  if (newValue.rowsPerPage != oldValue.rowsPerPage) {
    let first = (oldValue.page - 1) * oldValue.rowsPerPage + 1;
    var newFirst = 1;
    for (let i = newValue.rowsPerPage; i <= first; i += newValue.rowsPerPage) {
      newFirst = i / newValue.rowsPerPage + 1;
    }
    pagination.value.page = newFirst;
  }
  myStorage.setItem("pagination", JSON.stringify(pagination.value));
});

onMounted(updateUi);

const empty = computed(() => {
  return store.getters.getSavedStoryCardListLength == 0;
});

const displayModeObj = ref({
  displayMode: displayMode,
});
const isMobile = ref($q.platform.is.mobile);
const btnPad = ref($q.platform.is.mobile ? "8px 0 8px 5px" : "16px 0 16px 5px");
</script>
